import _ from 'lodash';
import Promise from 'bluebird';
import { singular, plural } from 'pluralize';
import URI from 'urijs';

// initial state
const createState = (helpers) => {
    const { api, uriFormat, getMessage, getErrorMessage, getConfig } = helpers;

    return api.getUser()
        .then(user => ({
            user,
            content: {},
            parent: null,
            sections: [],
            subSections: [],
            notifications: [],
            contentType: null,
            timestamp: Date.now(),
            loading: true,
            topic: 'Content',
            cache: {},
            queue: {},

            // multi-connections
            currentConnection: getConfig('currentConnection', ''),
            connections: getConfig('connections', []),
        }))
        .catch((e) => {
            if (_.get(e, 'response.status') == '401') {
                window.location.href = _.get(e, ['response', 'headers', 'www-authenticate'], '/admin/login');
            }
        })
}

// getters
const getters = {
    user: (state) => state.user,
    content: (state) => state.content,
    parent: (state) => state.parent,
    topic: (state) => state.topic,
    notifications: (state) => state.notifications,
    contentType: (state) => state.contentType,
    timestamp: (state) => state.timestamp,
    loading: (state) => state.loading,
    route: (_1, _2, rootState ) => _.get(rootState, 'route', {}),
    resourceParams: (_1, _2, rootState ) => _.get(rootState, 'route.params', {}),
    path: (_1, _2, rootState ) => _.get(rootState, 'route.path', {}),
    query: (_1, _2, rootState ) => _.get(rootState, 'route.query', {}),
    section: (_1, _2, rootState) => _.get(rootState, 'route.hash', '').replace('#', ''),
    cache: (state) => state.cache,

    // multi-connections
    currentConnection: (state) => state.currentConnection,
    connections: (state) => state.connections,
}

const createActions = (helpers) => {
    const { api, uriFormat, getMessage, getErrorMessage } = helpers;

    const getApiRoute = (route) => {
        return route;
    }

    const cacheData = (commit, cache, route, response, notCache = false) => {
        if (response.type == 'assets') return;

        if (!notCache) {
            commit('pushCache', { [route.fullPath]: response });
        }

        // const parent = _.get(response, 'included.parent');
        // if (parent) {
        //     commit('pushCache', { [uriFormat(parent.links.self)]: parent });
        // }

        // const relationships = _.get(response, 'links', {});
        // Promise.all(
        //     _.chain(relationships)
        //     .toPairs()
        //     .map(([relName, relLink]) => {
        //         const relPath = uriFormat(relLink);

        //         if (!relPath) return null;
        //         if (relPath == route.path) return null;
        //         if (cache[relPath]) return null;
        //         if (relName == 'self') return null;
        //         if (relName == 'new') return null;
        //         if (relName == 'collection') return null;

        //         return relPath;
        //     })
        //     .reject(_.isNull)
        //     .uniq()
        //     .map(relPath => {
        //         return api.getData(relPath)
        //             .then(relResponse => commit('pushCache', { [relPath]: relResponse }))
        //             .catch(console.log)
        //     })
        //     .value()
        // )   .then(() => {
        //     const parentRelationships = _.get(response, 'included.parent.links', {});

        //     Promise.all(_.toPairs(parentRelationships).map(([relName, relLink]) => {
        //         const relPath = uriFormat(relLink);

        //         if (!relPath) return null;
        //         if (relPath == route.path) return null;
        //         if (cache[relPath]) return null;

        //         return api.getData(relPath)
        //             .then(relResponse => commit('pushCache', { [relPath]: relResponse }))
        //             .catch(console.log)
        //     }))
        // })

        // const items = _.get(response, 'data', []);
        // if (items.length > 0) {
        //     Promise.all(items.map(item => {
        //         const itemPath = uriFormat(item.links.self);
        //         if (cache[itemPath]) return;
        //         return api.getData(itemPath)
        //             .then(itemResponse => {
        //                 commit('pushCache', { [itemPath]: itemResponse });
        //             })
        //             .catch(console.log);
        //     }))
        // }
    }

    const refreshCache = (commit, cache, path, response) => {
        let { type } = response;
        if (!type) return;
        type = type.split('.')[0];
        const types = [singular(type), plural(type)];

        let updateList = [];
        _.toPairs(cache)
            .forEach(([url, data]) => {
                const genericType = data.type.split('.')[0];
                if (url == path) {
                    return;
                }

                // disable aggressive update cache
                // if (types.indexOf(genericType) >= 0) {
                //     updateList.push(url);
                // }

                // _.values(data.included)
                //     .find(includedData => {
                //         const genericType = includedData.type.split('.')[0];

                //         if (types.indexOf(genericType) >= 0) {
                //             updateList.push(url);
                //             return true;
                //         }
                //     })
            });

        commit('flushCache', type);

        Promise.all(_.uniq(updateList).map(
            url => api.getData(url).then(response => ([url, response]))
        ))
            .catch(console.log)
            .then(pairs => _.fromPairs(pairs))
            .then(data => commit('pushCache', data));
    }

    // actions
    return {
        getInitialData ({ commit, rootState, state }) {
            const route = rootState.route;
            const { endpoint, id, relationship: relEndpoint, relationshipId: relId } = route.params;
            commit('processing');

            return api.getData(route.path, route.query)
                .then((dataResponse) => {
                    commit('setContent', dataResponse);
                    // cacheData(commit, state.cache, route, dataResponse);
                })
                .catch((error) => {
                    commit('setError', getErrorMessage(error, 'get'));
                    if (error.response.status == 401) {
                        window.location.href = '/admin/login';
                    }
                })
                .finally(() => commit('idle'))
        },

        getData ({ commit, rootState, state }, options = {}) {
            const route = getApiRoute(rootState.route)
            const { cache } = state;
            commit('processing');

            const cachedData = _.get(cache, route.fullPath);
            if (cachedData) {
                // cache further related data
                cacheData(commit, state.cache, route, cachedData, true);
                commit('setContent', cachedData);
                commit('idle');
                return cachedData;
            }

            return api
                .getData(route.path, route.query)
                .then((response) => {
                    commit('setContent', response);
                    cacheData(commit, state.cache, route, response);
                    return response;
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // if (options.setNotification) {
                    //     commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // } else {
                    //     commit('setError', getErrorMessage(error, 'get'));
                    // }
                    if (error.response.status == 401) {
                        window.location.href = '/admin/login';
                    }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        createData({ commit, state, rootState }, { data, options = {} }) {
            const route = getApiRoute(rootState.route)
            const { cache } = state;
            commit('processing');
            let apiMethod;

            if (options.upload) {
                apiMethod = api.uploadData(route.path, route.query, data);
            } else {
                apiMethod = api.postData(route.path, route.query, data)
            }
            return apiMethod
                .then((response) => {
                    // flush cache if not disable
                    if (!options.noCache) {
                        refreshCache(commit, cache, route.path, response);
                    }

                    if (options.addToCollection) {
                        commit('addToCollection', response);
                    } else {
                        commit('setContent', response);
                    }
                    if (options.setNotification) {
                        commit('setMessageNotification', getMessage(response, 'create'));
                    }
                    if (options.updateRoute) {
                        const path = uriFormat(response.links.self);
                        commit('pushCache', { [path]: response });
                        helpers.getRouter().push(path);
                    }
                    return response;
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'get'));

                    // merge form data to content so that field values will not varnish
                    commit('setContent', _.assign({}, state.content, { attributes: data }))
                    // if (options.setNotification) {
                    //     commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // } else {
                    //     commit('setError', getErrorMessage(error, 'get'));
                    // }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        updateBatch(store, { data, options = {} }) {
            const { commit, state, rootState } = store;
            const route = getApiRoute(rootState.route)
            const { cache } = state;
            commit('processing');

            return api
                .putData(route.path, route.query, { data })
                .then((response) => {
                    // flush cache
                    if (!options.noCache) {
                        refreshCache(commit, cache, route.path, response);
                    }

                    const parent = _.get(response, ['included', 'parent']);
                    if (parent && options.updateParent) {
                        refreshCache(commit, cache, null, parent);
                    }

                    if (options.subCollection) {
                        return store.dispatch('getData', options);
                    }

                    commit('updateCollection', response);
                    if (options.setNotification) {
                        commit('setMessageNotification', getMessage(response, 'updateBatch'));
                    }
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // if (options.setNotification) {
                    //     commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // } else {
                    //     commit('setError', getErrorMessage(error, 'get'));
                    // }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        updateData({ commit, state, rootState, getters }, { data, options = {} }) {
            const route = getApiRoute(rootState.route)
            const { cache } = state;
            commit('processing');

            return api
                .patchData(route.path, route.query, data)
                .then((response) => {
                    // flush cache
                    if (!options.noCache) {
                        refreshCache(commit, cache, route.path, response);
                    }

                    commit('setContent', response);
                    commit('pushCache', { [route.path]: response });

                    if (options.setNotification) {
                        commit('setMessageNotification', getMessage(response, 'update'));
                    }
                    return response;
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'get'));

                    // merge form data to content so that field values will not varnish
                    if (!options.resetAttributes) {
                        commit('setContent', _.merge(state.content, { attributes: data }))
                    }

                    // if (options.setNotification) {
                    //     commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // } else {
                    //     commit('setError', getErrorMessage(error, 'get'));
                    // }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        deleteData({ commit, state, rootState }, options = {}) {
            const route = getApiRoute(rootState.route)
            const { cache } = state;
            commit('processing');

            return api.delData(route.path, route.query)
                .then((response) => {
                    // flush cache
                    if (!options.noCache) {
                        refreshCache(commit, cache, route.path, response);
                    }

                    if (options.setNotification) {
                        commit('setMessageNotification', getMessage(response, 'delete'));
                    } else {
                        commit('setMessage', getMessage(response, 'delete'));
                    }

                    return response;
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // if (options.setNotification) {
                    //     commit('setErrorNotification', getErrorMessage(error, 'get'));
                    // } else {
                    //     commit('setError', getErrorMessage(error, 'get'));
                    // }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        replaceData({ commit }, data) {
            commit('updateCollection', {
                data: [data],
                meta: { updated: [data.id] },
            })
        },

        updateCollection({ commit }, data) {
            commit('updateCollection', { data,
                meta: {
                    updated: data.map(item => item.id)
                }
            })
        },

        addToCollection({ commit }, data) {
            commit('addToCollection', data);
        },

        updateParent({ commit, state }, data) {
            const { parent } = state;
            const { cache } = state;

            if (!parent) {
                commit('setErrorNotification', 'This content has no parent');
                return;
            }

            const path = uriFormat(parent.links.self);
            return api.patchData(path, {}, data)
                .then((response) => {
                    // flush cache
                    refreshCache(commit, cache, path, response);

                    commit('setParent', response);
                    commit('setMessageNotification', getMessage(response, 'update'));
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'update'));
                })
        },

        deleteParent({ commit, state }) {
            const { parent } = state;
            const { cache } = state;

            if (!parent) {
                commit('setErrorNotification', 'This content has no parent');
                return;
            }

            const path = uriFormat(parent.links.self);
            return api.delData(path)
                .then((response) => {
                    // flush cache
                    refreshCache(commit, cache, path, response);

                    // flush cache of current content and parent
                    commit('setMessageNotification', getMessage(response, 'delete'));
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'delete'));
                })
        },

        changeQuery({ commit, rootState }, queryData) {
            const { query, hash } = rootState.route;
            const newQuery = _.chain({}).assign(query, queryData).pickBy(_.identity).value();
            helpers.getRouter().push({ query: newQuery, hash });
        },

        changeSection({ commit, rootState }, section) {
            helpers.getRouter().push({ hash: `#${section.replace(/^#/, '')}` });
        },

        changeUrl({ commit }, url) {
            helpers.getRouter().push(uriFormat(url));
        },

        changePath({ commit }, url) {
            helpers.getRouter().push(url);
        },

        addNotification({ commit }, notification) {
            commit('addNotification', notification);
        },

        removeNotification({ commit }, index) {
            commit('removeNotification', index);
        },

        updateTopic({ commit }, topicName) {
            commit('updateTopic', topicName);
        },

        setContent({ commit }, content) {
            commit('setContent', content);
        },

        setParent({ commit }, parent) {
            commit('setParent', parent);
        },

        pushQueue({ commit }, payload) {
            commit('pushQueue', payload);
        },

        commitQueue({ commit, state, dispatch }) {
            const queue = _.get(state, 'queue', {});

            return Promise.all(_.chain(queue)
                .toPairs()
                .map(([action, payload]) => dispatch(action, payload))
            )
                .finally(() => commit('flushQueue'))
        },
    }
}

// mutations
const mutations = {
    updateCollection(state, response) {
        const { created = [], updated = [], deleted = [], attached = [], detached = [] } = response.meta;

        let data = state.content.data;

        // replacing updated items
        data = data.map(item => {
            const replacingItem = response.data.find(itemData => itemData.id == item.id);

            // not replace if item not in response
            if (!replacingItem) return item;

            const attributes = _.get(replacingItem, 'attributes') || _.get(item, 'attributes');
            const meta = _.get(replacingItem, 'meta') || _.get(item, 'meta');

            // only replace item attributes and meta to prevent
            // item links being replaced or removed
            return { ...replacingItem, attributes, meta };
        });

        // filter out deleted items
        data = data.filter(item => {
            return deleted.indexOf(item.id) < 0;
        });

        // add items created
        data = data.concat(
            response.data.filter(item => created.indexOf(item.id) >= 0)
        );

        // add items attached
        data = data.concat(
            response.data.filter(item => attached.indexOf(item.id) >= 0)
        );

        data = data.concat(
            response.data.filter(item => created.indexOf(item.id) >= 0)
        );

        // filter out detached items
        data = data.filter(item => {
            return detached.indexOf(item.id) < 0;
        });

        state.content.data = data;
        state.timestamp = Date.now();
        state.loading = false;
    },

    addToCollection (state, response) {
        state.content.data = state.content.data.concat([response]);
        state.timestamp = Date.now();
    },

    removeFromCollection (state, response) {
        state.content.data = state.content.data.filter(item => item.id !== response.id);
        state.timestamp = Date.now();
    },

    setUser (state, response) {
        state.user = response;
    },

    setContent (state, response) {
        state.content = response;
        state.parent = _.get(response, 'included.parent');
        state.timestamp = Date.now();
    },

    setParent (state, parent) {
        state.parent = parent;
    },

    setError (state, message) {
        state.content = {
            type: 'error',
            message,
        };
        state.timestamp = Date.now();
    },

    setErrorNotification (state, message) {
        state.notifications.push({
            type: 'error',
            message,
        });
        state.timestamp = Date.now();
    },

    setMessage (state, message) {
        state.content = {
            type: 'message',
            message,
        };
        state.timestamp = Date.now();
    },

    setMessageNotification(state, message) {
        state.notifications.push({
            type: 'message',
            message,
        });
        state.timestamp = Date.now();
    },

    addNotification(state, not) {
        state.notifications.push(not);
    },

    removeNotification(state, index) {
        state.notifications.splice(index, 1);
    },

    updateTopic(state, name) {
        state.topic = name;
    },

    pushCache(state, pair) {
        state.cache = { ...state.cache, ...pair };
    },

    flushCache(state, type) {
        const { cache } = state;
        state.cache = _.chain(cache).toPairs()
            .filter(([key, content]) => {
                if (!content) return false;

                let pass = singular(content.type) != singular(type);

                _.forEach(content.included, (includedData) => {
                    pass = pass && includedData && (singular(includedData.type) != singular(type));
                })

                return pass;
            })
            .fromPairs()
            .value();
    },

    pushQueue(state, payload) {
        const queue = _.get(state, 'queue', {});

        state.queue = _.mergeWith(queue, payload, (src, dst) => {
            if (!_.isArray(src)) return;

            const srcMap = _.keyBy(src, 'id');
            const dstMap = _.keyBy(dst, 'id');

            const result = _.merge(srcMap, dstMap);
            return _.values(result);
        })
    },

    flushQueue(state) {
        state.queue = {};
    },

    resetCache(state) {
        state.cache = {};
    },

    processing(state) {
        state.loading = true;
    },

    idle(state) {
        state.loading = false;
    },
}

export default (helpers) => {
    return Promise.all([
        createState(helpers),
        createActions(helpers),
    ])
        .then(([state, actions]) => ({
            state,
            getters,
            actions,
            mutations
        }))
}



// WEBPACK FOOTER //
// ./seventytwo/72cms-ui/src/store/modules/main.js